/**
 * Global Click Tracking Module
 * 
 * Implements automatic click tracking across the application using event delegation.
 * Respects GDPR two-tier consent system:
 * - Tier 1 (No Consent): Track only element type and page path (via trackAnonymousClick)
 * - Tier 2 (With Consent): Track full click data including text, href, session (via trackClick)
 * 
 * This module handles DOM event listening and data extraction, while delegating
 * the actual tracking to the unified tracking functions in trackEvents.ts and
 * anonymousTracking.ts for consistent data quality across all analytics.
 */

import { trackClick, isLovablePreview } from './trackEvents';
import { trackAnonymousClick } from './anonymousTracking';
import type { ClickEvent, ClickTrackingOptions } from './types';

// Module state
let isInitialized = false;
let currentOptions: ClickTrackingOptions | null = null;
let lastClickTime = 0;
const DEBOUNCE_MS = 300;

// Elements to ignore for click tracking
const IGNORED_ELEMENTS = ['INPUT', 'TEXTAREA', 'SELECT'];
const IGNORED_ROLES = ['textbox', 'searchbox', 'combobox'];

/**
 * Sanitize click text to remove potential PII
 * Only keeps first 50 chars and removes email-like patterns
 */
function sanitizeClickText(text: string): string {
  if (!text) return '';
  
  // Trim and limit length
  let sanitized = text.trim().substring(0, 50);
  
  // Remove email patterns
  sanitized = sanitized.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[email]');
  
  // Remove phone-like patterns
  sanitized = sanitized.replace(/[\+]?[(]?[0-9]{1,4}[)]?[-\s\./0-9]{6,}/g, '[phone]');
  
  return sanitized;
}

/**
 * Extract the clickable element from the event target
 * Walks up the DOM tree to find the nearest interactive element
 */
function findClickableElement(target: HTMLElement): HTMLElement | null {
  let element: HTMLElement | null = target;
  
  // Walk up to find the clickable ancestor (max 5 levels)
  for (let i = 0; i < 5 && element; i++) {
    const tagName = element.tagName;
    
    // Check if this is a clickable element
    if (
      tagName === 'BUTTON' ||
      tagName === 'A' ||
      element.getAttribute('role') === 'button' ||
      element.getAttribute('role') === 'link' ||
      element.hasAttribute('onclick') ||
      element.hasAttribute('data-track')
    ) {
      return element;
    }
    
    element = element.parentElement;
  }
  
  return null;
}

/**
 * Determine element type for analytics
 */
function getElementType(element: HTMLElement): 'button' | 'link' | 'icon' | 'other' {
  const tagName = element.tagName;
  const role = element.getAttribute('role');
  
  if (tagName === 'A' || role === 'link') return 'link';
  if (tagName === 'BUTTON' || role === 'button') return 'button';
  
  // Check for icon clicks (SVG or elements with icon-related classes)
  if (tagName === 'SVG' || element.querySelector('svg')) return 'icon';
  
  return 'other';
}

/**
 * Extract click event data from element
 */
function extractClickData(element: HTMLElement, pagePath: string): ClickEvent {
  const elementType = getElementType(element);
  const trackLabel = element.getAttribute('data-track');
  const trackCategory = element.getAttribute('data-track-category');
  
  // Get text content (prefer aria-label, then innerText)
  let elementText = element.getAttribute('aria-label') 
    || element.innerText 
    || element.getAttribute('title')
    || '';
  
  // Get href for links
  const elementHref = element.tagName === 'A' 
    ? element.getAttribute('href') 
    : element.closest('a')?.getAttribute('href');
  
  return {
    element_type: elementType,
    element_text: sanitizeClickText(elementText),
    element_href: elementHref || undefined,
    track_label: trackLabel || undefined,
    track_category: trackCategory || undefined,
    page_path: pagePath,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Global click event handler
 */
function handleGlobalClick(event: MouseEvent): void {
  if (!currentOptions) return;
  
  // Skip if in Lovable preview
  if (isLovablePreview(window.location.pathname + window.location.search)) return;
  
  const target = event.target as HTMLElement;
  if (!target) return;
  
  // Check for ignored elements
  if (IGNORED_ELEMENTS.includes(target.tagName)) return;
  if (IGNORED_ROLES.includes(target.getAttribute('role') || '')) return;
  if (target.getAttribute('contenteditable') === 'true') return;
  
  // Find the clickable element
  const clickableElement = findClickableElement(target);
  if (!clickableElement) return;
  
  // Check for opt-out
  if (clickableElement.hasAttribute('data-track-ignore')) return;
  
  // Debounce rapid clicks
  const now = Date.now();
  if (now - lastClickTime < DEBOUNCE_MS) return;
  lastClickTime = now;
  
  // Extract click data
  const pagePath = window.location.pathname;
  const clickData = extractClickData(clickableElement, pagePath);
  
  // Call custom handler if provided (for testing)
  if (currentOptions.onTrack) {
    currentOptions.onTrack(clickData);
  }
  
  // Track based on consent tier using unified tracking functions
  if (currentOptions.canTrack) {
    // Tier 2: Full tracking with session and user info
    trackClick(clickData, currentOptions.userId, currentOptions.sessionId);
  } else {
    // Tier 1: Anonymous tracking (no PII, no session)
    trackAnonymousClick(clickData);
  }
}

/**
 * Initialize global click tracking
 */
export function initGlobalClickTracking(options: ClickTrackingOptions): void {
  // Clean up existing listener if re-initializing
  if (isInitialized) {
    cleanupClickTracking();
  }
  
  currentOptions = options;
  
  // Add global listener with capture to catch all clicks
  document.addEventListener('click', handleGlobalClick, { capture: true, passive: true });
  
  isInitialized = true;
}

/**
 * Clean up global click tracking
 */
export function cleanupClickTracking(): void {
  if (!isInitialized) return;
  
  document.removeEventListener('click', handleGlobalClick, { capture: true } as EventListenerOptions);
  currentOptions = null;
  isInitialized = false;
  // Reset debounce timer to allow immediate clicks after cleanup
  lastClickTime = 0;
}

/**
 * Update tracking options (e.g., when consent changes)
 */
export function updateClickTrackingOptions(options: Partial<ClickTrackingOptions>): void {
  if (!currentOptions) return;
  currentOptions = { ...currentOptions, ...options };
}
